
import { arrayOutlineOf, ArrayProblem, muxSelectPortName, outlinePortsOf, joinOutPortName, joinInPortName } from "./ArrayExpand.js";
import { ofArray, singleton, filter as filter_1, find, reverse, toArray, length, fold, findIndex, item, cons, empty as empty_1, append, max, isEmpty, collect, map } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { LoadedComponent, CustomComponentType, CCForm, copiesOfArray, Connection, PortType, Port, Connection_$reflection, Component_$reflection, Component, ComponentType } from "../Common/CommonTypes.js";
import { defaultArg } from "../fable_modules/fable-library-js.5.13.0/Option.js";
import { fold as fold_1, map as map_2, tryFind, ofList, filter, add, empty } from "../fable_modules/fable-library-js.5.13.0/Map.js";
import { stringHash, curry2, compareArrays, comparePrimitives, equals, compare } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { ConstrainedExpr, tryFindSlot, ParamSlot, addSlot, ConstrainedExpr_$reflection, ParamSlot_$reflection, CompSlotName, ParamDefinition, ParamExpression, ParameterDefs } from "../Common/ParameterTypes.js";
import { fromInt32, fromZero } from "../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { FSharpRef, Record, Union } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { class_type, list_type, record_type, lambda_type, unit_type, union_type, int32_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { pToInt, cToInt } from "../Common/Ids.js";
import { map as map_1, delay, toList } from "../fable_modules/fable-library-js.5.13.0/Seq.js";
import { rangeDouble } from "../fable_modules/fable-library-js.5.13.0/Range.js";
import { item as item_1 } from "../fable_modules/fable-library-js.5.13.0/Array.js";
import { concat, printf, toFail } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { List_countBy } from "../fable_modules/fable-library-js.5.13.0/Seq2.js";
import { parseDiagramSignature } from "./CanvasExtractor.js";

export const Constants_maxArrayCopies = 1024;

export const Constants_copiesShownFlattened = 64;

const bodyMarker = "/";

/**
 * The name of the sheet holding one copy of an array design sheet.
 */
export function bodyNameOf(sheetName) {
    return (sheetName + bodyMarker) + "instance";
}

function bodyPortLabel(comp) {
    const matchValue = comp.Type;
    switch (matchValue.tag) {
        case 53:
            return joinInPortName(comp.Label, matchValue.fields[1]);
        case 52:
            return joinOutPortName(comp.Label, matchValue.fields[1]);
        default:
            return comp.Label;
    }
}

/**
 * The array design sheet as ONE copy: an ordinary sheet.
 * 
 * Every component keeps its id and its ports, so the connections are untouched and the sheet's
 * parameter slots - keyed by component id, and matching an IO slot by kind rather than by label -
 * apply to the body exactly as they applied to the sheet.
 */
export function bodyCanvasOf(_arg1_, _arg1__1) {
    const _arg = [_arg1_, _arg1__1];
    return [map((comp) => {
        let newType;
        const matchValue = comp.Type;
        let matchResult, w_1;
        switch (matchValue.tag) {
            case 53: {
                matchResult = 0;
                break;
            }
            case 52: {
                matchResult = 1;
                w_1 = matchValue.fields[0];
                break;
            }
            case 50: {
                matchResult = 1;
                w_1 = matchValue.fields[0];
                break;
            }
            case 51: {
                matchResult = 1;
                w_1 = matchValue.fields[0];
                break;
            }
            default:
                matchResult = 2;
        }
        switch (matchResult) {
            case 0: {
                newType = (new ComponentType(/* Input1 */ 0, [matchValue.fields[0], undefined]));
                break;
            }
            case 1: {
                newType = (new ComponentType(/* Output */ 1, [w_1]));
                break;
            }
            default:
                newType = undefined;
        }
        if (newType != null) {
            return new Component(comp.Id, newType, bodyPortLabel(comp), comp.InputPorts, comp.OutputPorts, comp.X, comp.Y, comp.H, comp.W, comp.SymbolInfo, comp.SlotInfo);
        }
        else {
            return comp;
        }
    }, _arg[0]), _arg[1]];
}

function bodyDefsOf(info, defs) {
    const sheetDefs = defaultArg(defs, new ParameterDefs(empty({
        Compare: (x, y) => (compare(x, y) | 0),
    }), empty({
        Compare: (x_1, y_1) => (compare(x_1, y_1) | 0),
    })));
    return new ParameterDefs(add(info.LoopParam, new ParamDefinition(new ParamExpression(/* PInt */ 0, [fromZero()]), "which copy of the array design sheet this is, counting from 0"), sheetDefs.DefaultBindings), filter((slot, _arg) => !equals(slot.CompSlot, CompSlotName.JoinNum), sheetDefs.ParamSlots));
}

class OutputDriver extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["FromCopy", "FromMerge", "FromMux"];
    }
}

function OutputDriver_$reflection() {
    return union_type("ArrayElaborate.OutputDriver", [], OutputDriver, () => [[["Copy", int32_type], ["BodyPort", int32_type]], [["BodyPort", int32_type]], [["BodyPort", int32_type], ["Select", Component_$reflection()]]]);
}

class IdSource extends Record {
    constructor(NewComp, NewPort, NewConn) {
        super();
        this.NewComp = NewComp;
        this.NewPort = NewPort;
        this.NewConn = NewConn;
    }
}

function IdSource_$reflection() {
    return record_type("ArrayElaborate.IdSource", [], IdSource, () => [["NewComp", lambda_type(unit_type, int32_type)], ["NewPort", lambda_type(unit_type, int32_type)], ["NewConn", lambda_type(unit_type, int32_type)]]);
}

function firstFreeIds(ldcs) {
    const maxOf = (f) => {
        const _arg = collect(f, ldcs);
        if (isEmpty(_arg)) {
            return 0;
        }
        else {
            return max(_arg, {
                Compare: (x, y) => (comparePrimitives(x, y) | 0),
            }) | 0;
        }
    };
    return [maxOf((ldc) => map((c) => (cToInt(c.Id) | 0), ldc.CanvasState[0])) + 1, maxOf((ldc_1) => collect((c_1) => map((p) => (pToInt(p.Id) | 0), append(c_1.InputPorts, c_1.OutputPorts)), ldc_1.CanvasState[0])) + 1, maxOf((ldc_2) => map((c_2) => c_2.Id, ldc_2.CanvasState[1])) + 1];
}

function withIdSource(ldcs, body) {
    const patternInput = firstFreeIds(ldcs);
    const comp = new FSharpRef(patternInput[0]);
    const port = new FSharpRef(patternInput[1]);
    const conn = new FSharpRef(patternInput[2]);
    const next = (counter) => {
        const taken = counter.contents | 0;
        counter.contents = ((taken + 1) | 0);
        return taken | 0;
    };
    return body(new IdSource(() => (next(comp) | 0), () => (next(port) | 0), () => (next(conn) | 0)));
}

class Build extends Record {
    constructor(Comps, Conns, Slots, Row) {
        super();
        this.Comps = Comps;
        this.Conns = Conns;
        this.Slots = Slots;
        this.Row = (Row | 0);
    }
}

function Build_$reflection() {
    return record_type("ArrayElaborate.Build", [], Build, () => [["Comps", list_type(Component_$reflection())], ["Conns", list_type(Connection_$reflection())], ["Slots", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [ParamSlot_$reflection(), ConstrainedExpr_$reflection()])], ["Row", int32_type]]);
}

const emptyBuild = new Build(empty_1(), empty_1(), empty({
    Compare: (x, y) => (compare(x, y) | 0),
}), 0);

function addComp(ids, b, compType, label, nIn, nOut) {
    const id = ids.NewComp();
    const ports = (n, portType) => toList(delay(() => map_1((i) => (new Port(ids.NewPort(), i, portType, id)), rangeDouble(0, 1, n - 1))));
    const comp = new Component(id, compType, label, ports(nIn, PortType.Input), ports(nOut, PortType.Output), 0, b.Row * 100, 30, 60, undefined, undefined);
    return [comp, new Build(cons(comp, b.Comps), b.Conns, b.Slots, b.Row + 1)];
}

function wire(ids, b, src, srcPort, tgt, tgtPort) {
    let bind$0040, bind$0040_1;
    return new Build(b.Comps, cons(new Connection(ids.NewConn(), (bind$0040 = item(srcPort, src.OutputPorts), new Port(bind$0040.Id, undefined, bind$0040.PortType, bind$0040.HostId)), (bind$0040_1 = item(tgtPort, tgt.InputPorts), new Port(bind$0040_1.Id, undefined, bind$0040_1.PortType, bind$0040_1.HostId)), empty_1()), b.Conns), b.Slots, b.Row);
}

function addWidthSlot(b, comp, expr) {
    if (expr != null) {
        return new Build(b.Comps, b.Conns, addSlot(new ParamSlot(comp.Id, new CompSlotName(/* IO */ 1, [comp.Label])), expr, b.Slots), b.Row);
    }
    else {
        return b;
    }
}

function wrapperOf(ids, sheet, info, bodyName, _arg1_, _arg1__1) {
    const _arg = [_arg1_, _arg1__1];
    const bodyOuts = _arg[1];
    const bodyIns = _arg[0];
    const addComp_1 = (b, compType, label, nIn, nOut) => addComp(ids, b, compType, label, nIn, nOut);
    const wire_1 = (b_1, src, srcPort, tgt, tgtPort) => wire(ids, b_1, src, srcPort, tgt, tgtPort);
    const copies = copiesOfArray(info) | 0;
    let patternInput;
    const tupledArg = sheet.CanvasState;
    patternInput = outlinePortsOf(info, sheet.LCParameterSlots, tupledArg[0], tupledArg[1]);
    const wiring = patternInput[1];
    const sheetDefs = defaultArg(sheet.LCParameterSlots, new ParameterDefs(empty({
        Compare: (x, y) => (compare(x, y) | 0),
    }), empty({
        Compare: (x_1, y_1) => (compare(x_1, y_1) | 0),
    })));
    const bodyIn = (comp) => (findIndex((tupledArg_1) => (tupledArg_1[0] === bodyPortLabel(comp)), bodyIns) | 0);
    const bodyOut = (comp_1) => (findIndex((tupledArg_2) => (tupledArg_2[0] === bodyPortLabel(comp_1)), bodyOuts) | 0);
    let copyPortNames;
    const byBodyLabel = ofList(map((e) => [[bodyPortLabel(e.Comp), e.Copy], e], wiring.Ends), {
        Compare: (x_2, y_2) => (compareArrays(x_2, y_2) | 0),
    });
    const renamed = (copy, _arg_3) => {
        const width = _arg_3[1] | 0;
        const label_1 = _arg_3[0];
        const matchValue = tryFind([label_1, copy], byBodyLabel);
        if (matchValue != null) {
            const e_1 = matchValue;
            if (e_1.Comp.Type.tag === 53) {
                return [joinInPortName(e_1.Comp.Label, e_1.Num), width];
            }
            else {
                return [joinOutPortName(e_1.Comp.Label, e_1.Num), width];
            }
        }
        else {
            return [label_1, width];
        }
    };
    copyPortNames = ((copy_1) => [map(curry2(renamed)(copy_1), bodyIns), map(curry2(renamed)(copy_1), bodyOuts)]);
    const widthExprOf = (comp_2) => tryFindSlot(new ParamSlot(comp_2.Id, new CompSlotName(/* IO */ 1, [comp_2.Label])), sheetDefs.ParamSlots);
    let patternInput_2;
    const tupledArg_4 = fold((tupledArg_3, i_1) => {
        let table_1;
        const _arg_5 = tupledArg_3[0];
        const patternInput_1 = addComp_1(tupledArg_3[1], new ComponentType(/* Custom */ 26, [new CustomComponentType(bodyName, bodyIns, bodyOuts, CCForm.User, (table_1 = map_2((name, _arg_4) => (new ParamExpression(/* PParameter */ 1, [name])), sheetDefs.DefaultBindings), add(info.LoopParam, new ParamExpression(/* PInt */ 0, [fromInt32(i_1)]), table_1)), undefined)]), `${sheet.Name}${i_1}`, length(bodyIns), length(bodyOuts));
        const comp_3 = patternInput_1[0];
        return [[cons(comp_3, _arg_5[0]), cons([comp_3.Id, copyPortNames(i_1)], _arg_5[1])], patternInput_1[1]];
    }, [[empty_1(), empty_1()], emptyBuild], toList(rangeDouble(0, 1, copies - 1)));
    const _arg_6 = tupledArg_4[0];
    patternInput_2 = [toArray(reverse(_arg_6[0])), ofList(_arg_6[1], {
        Compare: (x_3, y_3) => (compare(x_3, y_3) | 0),
    }), tupledArg_4[1]];
    const copyComps = patternInput_2[0];
    const widthExprFor = (p) => {
        let e_2;
        const matchValue_2 = p.Role;
        switch (matchValue_2.tag) {
            case 2:
                return undefined;
            case 4: {
                const option_2 = widthExprOf(p.Comp);
                if (option_2 != null) {
                    return (e_2 = option_2, new ConstrainedExpr(new ParamExpression(/* PMultiply */ 4, [new ParamExpression(/* PInt */ 0, [fromInt32(copies)]), e_2.Expression]), e_2.Constraints));
                }
                else {
                    return undefined;
                }
            }
            default:
                return widthExprOf(p.Comp);
        }
    };
    const b_10 = fold((b_6, p_1) => {
        let matchValue_3;
        const patternInput_3 = addComp_1(b_6, new ComponentType(/* Input1 */ 0, [p_1.Width, undefined]), p_1.Name, 0, 1);
        const comp_4 = patternInput_3[0];
        return fold((b_9, copy_2) => wire_1(b_9, comp_4, 0, item_1(copy_2, copyComps), bodyIn(p_1.Comp)), addWidthSlot(patternInput_3[1], comp_4, widthExprFor(p_1)), (matchValue_3 = p_1.Role, (matchValue_3.tag === 0) ? toList(rangeDouble(0, 1, copies - 1)) : ((matchValue_3.tag === 1) ? matchValue_3.fields[0] : empty_1())));
    }, patternInput_2[2], patternInput[0][0]);
    let patternInput_5;
    const tupledArg_6 = fold((tupledArg_5, p_2) => {
        let matchValue_4;
        const patternInput_4 = addComp_1(tupledArg_5[1], new ComponentType(/* Output */ 1, [p_2.Width]), p_2.Name, 1, 0);
        const comp_5 = patternInput_4[0];
        const b_13 = addWidthSlot(patternInput_4[1], comp_5, widthExprFor(p_2));
        return [cons([comp_5, (matchValue_4 = p_2.Role, (matchValue_4.tag === 4) ? (new OutputDriver(/* FromMerge */ 1, [bodyOut(p_2.Comp)])) : ((matchValue_4.tag === 5) ? (new OutputDriver(/* FromMux */ 2, [bodyOut(p_2.Comp), find((c) => (c.Label === muxSelectPortName(p_2.Comp.Label)), b_10.Comps)])) : ((matchValue_4.tag === 0) ? toFail(printf("%A is an input role and cannot drive the output \'%s\'"))(p_2.Role)(p_2.Name) : ((matchValue_4.tag === 1) ? toFail(printf("%A is an input role and cannot drive the output \'%s\'"))(p_2.Role)(p_2.Name) : ((matchValue_4.tag === 2) ? toFail(printf("%A is an input role and cannot drive the output \'%s\'"))(p_2.Role)(p_2.Name) : (new OutputDriver(/* FromCopy */ 0, [matchValue_4.fields[0], bodyOut(p_2.Comp)])))))))], tupledArg_5[0]), b_13];
    }, [empty_1(), b_10], patternInput[0][1]);
    patternInput_5 = [reverse(tupledArg_6[0]), tupledArg_6[1]];
    const b_26 = fold((b_25, tupledArg_8) => {
        const outEnd = tupledArg_8[0];
        const inEnd = tupledArg_8[1];
        return wire_1(b_25, item_1(outEnd.Copy, copyComps), bodyOut(outEnd.Comp), item_1(inEnd.Copy, copyComps), bodyIn(inEnd.Comp));
    }, fold((b_16, tupledArg_7) => {
        const outComp = tupledArg_7[0];
        const driver_1 = tupledArg_7[1];
        switch (driver_1.tag) {
            case 1: {
                const patternInput_6 = addComp_1(b_16, new ComponentType(/* ArrayMerge */ 54, [copies]), concat(outComp.Label, "_merge"), copies, 1);
                const merge = patternInput_6[0];
                return wire_1(fold((b_18, i_2) => wire_1(b_18, item_1(i_2, copyComps), driver_1.fields[0], merge, i_2), patternInput_6[1], toList(rangeDouble(0, 1, copies - 1))), merge, 0, outComp, 0);
            }
            case 2: {
                const patternInput_7 = addComp_1(b_16, new ComponentType(/* ArrayMux */ 55, [copies]), concat(outComp.Label, "_mux"), copies + 1, 1);
                const mux = patternInput_7[0];
                return wire_1(wire_1(fold((b_21, i_3) => wire_1(b_21, item_1(i_3, copyComps), driver_1.fields[0], mux, i_3), patternInput_7[1], toList(rangeDouble(0, 1, copies - 1))), driver_1.fields[1], 0, mux, copies), mux, 0, outComp, 0);
            }
            default:
                return wire_1(b_16, item_1(driver_1.fields[0], copyComps), driver_1.fields[1], outComp, 0);
        }
    }, patternInput_5[1], patternInput_5[0]), wiring.Matched);
    return [[reverse(b_26.Comps), reverse(b_26.Conns)], new ParameterDefs(sheetDefs.DefaultBindings, b_26.Slots), patternInput_2[1]];
}

function bodyNameProblems(sheetName, _arg1_, _arg1__1) {
    const comps = _arg1_;
    return map((tupledArg_1) => {
        const label = tupledArg_1[0];
        return new ArrayProblem(concat("Two components would both be port \'", label, "\' of a copy"), map((c_1) => c_1.Id, filter_1((c) => (bodyPortLabel(c) === label), comps)));
    }, filter_1((tupledArg) => (tupledArg[1] > 1), List_countBy(bodyPortLabel, filter_1((comp) => {
        const matchValue = comp.Type;
        switch (matchValue.tag) {
            case 0:
            case 1:
            case 50:
            case 51:
            case 52:
            case 53:
                return true;
            default:
                return false;
        }
    }, comps), {
        Equals: (x, y) => (x === y),
        GetHashCode: (x) => (stringHash(x) | 0),
    })));
}

/**
 * Every array design sheet replaced by the two ordinary sheets it expands to, and everything that
 * is wrong with any of them - each problem paired with the sheet it is about, so that a caller can
 * tell which of them the design being simulated actually depends on.
 * 
 * After this the simulator has never seen an array sheet: BusOut, MuxOut, JoinOut and JoinIn are
 * gone from every canvas, and what is left is custom components, wires and the two array glue
 * types. That is why the evaluators, the truth table and the dependency machinery need no part in
 * this, and why the wave selector shows the copies without being told about arrays.
 */
export function expandArraySheets(ldcs) {
    return withIdSource(ldcs, (ids) => {
        const tupledArg_4 = fold((tupledArg_3, ldc) => {
            let tupledArg_1, tupledArg_2;
            const sheets = tupledArg_3[0];
            const problems_1 = tupledArg_3[1];
            const names = tupledArg_3[2];
            const matchValue = ldc.ArrayInfo;
            if (matchValue != null) {
                let patternInput_4;
                const sheet = ldc;
                const info = matchValue;
                const copies = copiesOfArray(info) | 0;
                let sizeProblems;
                const asProblem = (msg) => singleton(new ArrayProblem(msg, empty_1()));
                sizeProblems = ((copies < 1) ? asProblem("Number of copies must be at least 1") : ((copies > Constants_maxArrayCopies) ? asProblem(`Number of copies must be at most ${Constants_maxArrayCopies}`) : empty_1()));
                if (isEmpty(sizeProblems)) {
                    let bodyCanvas;
                    const tupledArg = sheet.CanvasState;
                    bodyCanvas = bodyCanvasOf(tupledArg[0], tupledArg[1]);
                    const patternInput = parseDiagramSignature(bodyCanvas[0], bodyCanvas[1]);
                    const bodyOuts = patternInput[1];
                    const bodyIns = patternInput[0];
                    const bodyName = bodyNameOf(sheet.Name);
                    const body = new LoadedComponent(bodyName, sheet.TimeStamp, sheet.FilePath, sheet.WaveInfo, bodyCanvas, bodyIns, bodyOuts, bodyDefsOf(info, sheet.LCParameterSlots), sheet.Form, sheet.LoadedComponentIsOutOfDate, sheet.Description, false, undefined);
                    const patternInput_1 = wrapperOf(ids, sheet, info, bodyName, bodyIns, bodyOuts);
                    const canvas = patternInput_1[0];
                    const patternInput_2 = parseDiagramSignature(canvas[0], canvas[1]);
                    patternInput_4 = [ofArray([new LoadedComponent(sheet.Name, sheet.TimeStamp, sheet.FilePath, sheet.WaveInfo, canvas, patternInput_2[0], patternInput_2[1], patternInput_1[1], sheet.Form, sheet.LoadedComponentIsOutOfDate, sheet.Description, sheet.IsTopSheet, undefined), body]), map((p) => [sheet.Name, new ArrayProblem(concat("Array component \'", sheet.Name, "\': ", p.Message), p.Components)], append(((tupledArg_1 = sheet.CanvasState, arrayOutlineOf(info, sheet.LCParameterSlots, tupledArg_1[0], tupledArg_1[1])))[1], (tupledArg_2 = sheet.CanvasState, bodyNameProblems(sheet.Name, tupledArg_2[0], tupledArg_2[1])))), patternInput_1[2]];
                }
                else {
                    patternInput_4 = [singleton(sheet), map((msg_1) => [sheet.Name, msg_1], sizeProblems), empty({
                        Compare: (x, y) => (compare(x, y) | 0),
                    })];
                }
                return [append(reverse(patternInput_4[0]), sheets), append(problems_1, patternInput_4[1]), fold_1((acc, k, v) => add(k, v, acc), names, patternInput_4[2])];
            }
            else {
                return [cons(ldc, sheets), problems_1, names];
            }
        }, [empty_1(), empty_1(), empty({
            Compare: (x_1, y_1) => (compare(x_1, y_1) | 0),
        })], ldcs);
        return [reverse(tupledArg_4[0]), tupledArg_4[1], tupledArg_4[2]];
    });
}

